import React, { ErrorInfo, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { AlertCircle } from "lucide-react";
import { Button } from "components/ui/button";
import Logger from "../redux/helpers/logger";
import ErrorTracker from "../redux/helpers/errorTracker";

/**
 * What the customer sees instead of a white screen when a render throws.
 *
 * Deliberately depends on nothing a crash could have taken down: no router,
 * no store, no MUI theme — only i18n (initialised before the first render)
 * and Tailwind tokens.
 */
export const AppErrorFallback = () => {
  const { t } = useTranslation();

  return (
    <div
      role="alert"
      className="flex min-h-screen flex-col items-center justify-center bg-background px-6 py-12"
    >
      <div className="w-full max-w-md space-y-6 text-center">
        <AlertCircle className="mx-auto h-12 w-12 text-foreground/60" />
        <div className="space-y-2">
          <h1 className="text-xl font-semibold text-foreground">
            {t("error.app_crash_title")}
          </h1>
          <p className="text-foreground/70">{t("error.app_crash_info")}</p>
        </div>
        <Button
          className="w-full"
          size="lg"
          onClick={() => window.location.reload()}
        >
          {t("error.app_crash_reload")}
        </Button>
      </div>
    </div>
  );
};

interface AppErrorBoundaryState {
  hasError: boolean;
}

/**
 * Root error boundary. Without one, any exception thrown while rendering
 * unmounts the whole React tree and leaves the customer on a blank page with
 * nothing to press.
 *
 * The error is logged to the browser console. The `ErrorTracker` call reaches
 * nobody today: end-user never calls `Sentry.init` and has no DSN configured,
 * so `captureException` is a no-op until that is set up.
 */
class AppErrorBoundary extends React.Component<
  { children: ReactNode },
  AppErrorBoundaryState
> {
  state: AppErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): AppErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    Logger.error("Unhandled render error", error, info.componentStack);
    ErrorTracker.trackException(error, {
      componentStack: info.componentStack,
    });
  }

  render() {
    if (this.state.hasError) {
      return <AppErrorFallback />;
    }
    return this.props.children;
  }
}

export default AppErrorBoundary;
